import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Save, AlertTriangle, CheckCircle, GitCompare, FileText } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_COLORS = { draft: 'bg-slate-100 text-slate-600', internal_review: 'bg-blue-50 text-blue-700', customer_review: 'bg-amber-50 text-amber-700', revision_needed: 'bg-orange-50 text-orange-700', approved_layout: 'bg-emerald-50 text-emerald-700', ready_for_engineering: 'bg-violet-50 text-violet-700', archived: 'bg-gray-100 text-gray-400' };
const STAGE_LABELS = { draft: 'Draft Plan', internal_review: 'Internal Review', customer_review: 'Customer Review', revision_needed: 'Revision Needed', approved_layout: 'Approved Layout', ready_for_engineering: 'Ready for Engineering', archived: 'Archived' };
const SEV_COLORS = { informational: 'bg-slate-100 text-slate-600', internal_review: 'bg-blue-50 text-blue-700', proposal_revision_needed: 'bg-orange-50 text-orange-700', customer_approval_needed: 'bg-red-50 text-red-700', pricing_update_needed: 'bg-red-50 text-red-700' };

export default function PlanReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [diff, setDiff] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get(`/plans/${id}`);
    setPlan(r.data);
    const d = await api.get(`/plans/${id}/scope-diff`);
    setDiff(d.data);
  };
  useEffect(() => { if (id) load(); }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/plans/${id}`, plan);
      toast.success('Plan saved');
      load();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const u = (field, value) => setPlan(p => ({ ...p, [field]: value }));

  if (!plan) return <div className="p-8"><div className="h-64 bg-white rounded-sm animate-pulse" /></div>;

  const changes = diff?.changes || plan.scope_changes || [];
  const majorChanges = changes.filter(c => c.is_major);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="plan-review-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-sm"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl text-[#0F1D32]">Plan: {plan.customer_name}</h1>
            <Badge className={`text-[10px] font-bold rounded-sm ${STAGE_COLORS[plan.stage] || STAGE_COLORS.draft}`}>{STAGE_LABELS[plan.stage] || plan.stage}</Badge>
            <Badge className="bg-[#0F1D32]/10 text-[#0F1D32] text-[10px] rounded-sm">V{plan.version} — {plan.plan_type}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1" style={{fontFamily:'Manrope',fontStyle:'normal'}}>{plan.project_address}, {plan.city} | ${plan.estimated_total?.toLocaleString()} estimated</p>
        </div>
        <Select value={plan.stage} onValueChange={v => u('stage', v)}>
          <SelectTrigger className="w-48 rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(STAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wider text-xs rounded-sm">
          <Save className="w-4 h-4 mr-1" /> Save Plan
        </Button>
      </div>

      {/* Change Alerts */}
      {majorChanges.length > 0 && (
        <Card className="border-2 border-orange-300 bg-orange-50 rounded-sm mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <p className="text-sm font-bold text-orange-800" style={{fontFamily:'Manrope',fontStyle:'normal'}}>{majorChanges.length} Scope Change{majorChanges.length > 1 ? 's' : ''} vs Proposal</p>
            </div>
            <div className="space-y-1">
              {majorChanges.map(c => (
                <div key={c.field} className="flex items-center gap-2 text-xs">
                  <Badge className={`text-[8px] rounded-sm ${SEV_COLORS[c.severity] || SEV_COLORS.informational}`}>{c.severity.replace(/_/g, ' ')}</Badge>
                  <span className="font-bold text-orange-800">{c.label}:</span>
                  <span className="text-orange-600">{String(c.from_value || 'none')} → {String(c.to_value || 'none')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SECTION 1 — Proposal-Sourced Scope */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-500" />
              <CardTitle className="text-base" style={{fontFamily:'Cormorant Garamond',fontStyle:'italic'}}>Proposal-Sourced Scope</CardTitle>
            </div>
            <p className="eyebrow mt-1">FROM PROPOSAL</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Pool Type</Label><Input value={plan.pool_type} onChange={e => u('pool_type', e.target.value)} className="rounded-sm" /></div>
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Dimensions</Label>
                <div className="flex gap-2">
                  <Input type="number" value={plan.length} onChange={e => u('length', parseFloat(e.target.value) || 0)} className="rounded-sm" placeholder="L" />
                  <Input type="number" value={plan.width} onChange={e => u('width', parseFloat(e.target.value) || 0)} className="rounded-sm" placeholder="W" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Avg Depth</Label><Input type="number" step="0.5" value={plan.avg_depth} onChange={e => u('avg_depth', parseFloat(e.target.value) || 0)} className="rounded-sm" /></div>
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Equipment</Label><Input value={plan.equipment_package} onChange={e => u('equipment_package', e.target.value)} className="rounded-sm" /></div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2"><Switch checked={plan.spa_included} onCheckedChange={v => u('spa_included', v)} /><Label className="text-xs font-bold">Spa</Label></div>
              <div className="flex items-center gap-2"><Switch checked={plan.tanning_ledge} onCheckedChange={v => u('tanning_ledge', v)} /><Label className="text-xs font-bold">Tanning Ledge</Label></div>
              <div className="flex items-center gap-2"><Switch checked={plan.cover_included} onCheckedChange={v => u('cover_included', v)} /><Label className="text-xs font-bold">Cover</Label></div>
            </div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Interior Finish</Label><Input value={plan.interior_finish} onChange={e => u('interior_finish', e.target.value)} className="rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Deck SF</Label><Input type="number" value={plan.deck_sqft} onChange={e => u('deck_sqft', parseInt(e.target.value) || 0)} className="rounded-sm" /></div>
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Deck Material</Label><Input value={plan.deck_material} onChange={e => u('deck_material', e.target.value)} className="rounded-sm" /></div>
            </div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Upgrades / Features</Label><Input value={(plan.selected_upgrades || []).join(', ')} readOnly className="rounded-sm bg-[#F8F6F3]" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Utility Assumptions</Label><Input value={plan.utility_assumptions} readOnly className="rounded-sm bg-[#F8F6F3]" /></div>
          </CardContent>
        </Card>

        {/* SECTION 2 — Layout Details Still Needed */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <GitCompare className="w-4 h-4 text-amber-500" />
              <CardTitle className="text-base" style={{fontFamily:'Cormorant Garamond',fontStyle:'italic'}}>Layout & Plan Details</CardTitle>
            </div>
            <p className="eyebrow mt-1">FILL IN FOR PLAN</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Pool Orientation</Label><Input value={plan.pool_orientation} onChange={e => u('pool_orientation', e.target.value)} placeholder="N/S, E/W, diagonal..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Lot Placement</Label><Input value={plan.lot_placement} onChange={e => u('lot_placement', e.target.value)} placeholder="Back center, offset left..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Setbacks</Label><Input value={plan.setbacks} onChange={e => u('setbacks', e.target.value)} placeholder="5ft from fence, 10ft from house..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Equipment Pad Location</Label><Input value={plan.equipment_pad_location} onChange={e => u('equipment_pad_location', e.target.value)} placeholder="Right side of house..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Utility Route Notes</Label><Textarea value={plan.utility_route_notes} onChange={e => u('utility_route_notes', e.target.value)} placeholder="Electric from south panel, gas from west..." className="rounded-sm" rows={2} /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Drainage Notes</Label><Input value={plan.drainage_notes} onChange={e => u('drainage_notes', e.target.value)} placeholder="Slopes toward..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Spa Placement</Label><Input value={plan.spa_placement} onChange={e => u('spa_placement', e.target.value)} placeholder="Raised, corner, attached..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Step / Entry Location</Label><Input value={plan.step_location} onChange={e => u('step_location', e.target.value)} placeholder="Corner entry, full-width..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Depth Profile</Label><Input value={plan.depth_profile} onChange={e => u('depth_profile', e.target.value)} placeholder="3.5ft shallow, 5.5ft deep..." className="rounded-sm" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Access / Excavation Notes</Label><Textarea value={plan.access_notes} onChange={e => u('access_notes', e.target.value)} placeholder="Gate access on left, no crane needed..." className="rounded-sm" rows={2} /></div>
          </CardContent>
        </Card>
      </div>

      {/* SECTION 3 — Scope Comparison */}
      {changes.length > 0 && (
        <Card className="border-slate-200 rounded-sm mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base" style={{fontFamily:'Cormorant Garamond',fontStyle:'italic'}}>Scope Changes vs Proposal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {changes.map(c => (
                <div key={c.field} className="flex items-center justify-between p-2.5 bg-[#F8F6F3] rounded-sm">
                  <div className="flex items-center gap-2">
                    {c.is_major ? <AlertTriangle className="w-3.5 h-3.5 text-orange-500" /> : <CheckCircle className="w-3.5 h-3.5 text-slate-400" />}
                    <span className="text-xs font-bold text-[#0F1D32]">{c.label}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-slate-500 line-through">{String(c.from_value || 'none')}</span>
                    <span className="text-[#0F1D32] font-bold">→ {String(c.to_value || 'none')}</span>
                    <Badge className={`text-[8px] rounded-sm ${SEV_COLORS[c.severity]}`}>{c.severity.replace(/_/g, ' ')}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
